import React from 'react';
import { gaeApp } from './Analytics';
import PropTypes from 'prop-types';
import { xconnect } from '../../redux/xconnect';
import {
  DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW,
  DOCUMENT_DISPLAY_MODE_MOBILE_WINDOW,
  DOC_TYPE_INFORMATION
} from './utils';
import { confirmDialog } from './dialogUtils';
import II18 from '../translate/II18';
import {
  setDocumentDataUrl,
  setDocumentVisited,
  unSetDocumentRejected
} from '../../redux/actions/rootAction';
import withDocumentRootLogic from './withDocumentRootLogic';
import { create_UUID } from '../../redux/utils';
import { isDocumentVisited } from './formUtils';

export default function withDocumentCommon(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class withDocumentCommonWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.componentId = create_UUID();
      // Snapshot before componentDidMount marks this open as visited.
      this.documentWasVisited = isDocumentVisited(
        props.visitedDocuments,
        props.requestedDocumentId
      );
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (
        this.props.rejectedDocuments?.size !== prevProps.rejectedDocuments?.size
      ) {
        this.onDocumentRejected();
      }
    }

    componentDidMount() {
      if (this.props.requestedDocumentId) {
        this.props.setDocumentVisited(this.props.requestedDocumentId);

        gaeApp(
          'DOCUMENT_START_STATS',
          JSON.stringify(this.getRequestedDocument()?.stats, null, 2)
        );
      }
    }

    getRequestedDocument = () => {
      return this.props.requestedDocuments.find(
        doc => doc.id === this.props.requestedDocumentId
      );
    };

    sendFromDocumentView = () => {
      const document = this.getRequestedDocument();
      const isInfoDocument = document?.type === DOC_TYPE_INFORMATION;
      this.props.doSendRequestFromDocumentView({
        noProgressDialog: isInfoDocument,
        skipThankyou: isInfoDocument
      });
    };

    /**
     * @param {boolean} [skipConfirm]
     * @param {string|false} [confirmCustomMessage]
     * @param {{ allowAdvanceDespiteNotReady?: boolean }} [options]
     *   Thin lag bypass from forms after stamp write; no form/stamp logic here.
     */
    onDocumentDone = (skipConfirm, confirmCustomMessage, options) => {
      gaeApp('START_ON_DOCUMENT_DONE');
      gaeApp(
        'DOCUMENT_DONE_STATS',
        JSON.stringify(this.getRequestedDocument().stats, null, 2)
      );

      const document = this.getRequestedDocument();
      const allowAdvanceDespiteNotReady =
        options?.allowAdvanceDespiteNotReady === true;

      if (
        !document.optional &&
        !document.ready &&
        !document.rejected &&
        !allowAdvanceDespiteNotReady
      ) {
        gaeApp('ON_DOCUMENT_DONE_ERROR.DOCUMENT_IS_NOT_READY');
        this.$f7.dialog.alert(II18.get('technical_error_hard'));
        return;
      }

      const confirmPrompt = yesFunc =>
        confirmDialog(
          II18.get('title_done_request'),
          confirmCustomMessage || II18.get('title_done_request_confirm'),
          yesFunc,
          null,
          II18.get('title_confirm')
        );

      //TODO: all these logic should be in RootDocumentLogic
      if (this.props.crossDeviceRequest) {
        this.props.navigateBack();
      } else if (
        this.props.isSkipDocumentListMode &&
        !this.props.requestPages
      ) {
        gaeApp('SEND_REQUEST_ON_DOCUMENT_DONE');
        if (
          !skipConfirm &&
          (!this.props.templateMode || this.props.usersDataOk)
        ) {
          confirmPrompt(this.sendFromDocumentView);
        } else {
          this.sendFromDocumentView();
        }
      } else {
        if (!window.isDesktop && this.props.requestPages) {
          this.props.navigateToRequestNextPageFromMobileDocument();
        } else this.props.navigateBack();
      }
      gaeApp('END_ON_DOCUMENT_DONE');
    };

    navigateBack = () => {
      gaeApp(
        'DOCUMENT_CLOSE_STATS',
        JSON.stringify(this.getRequestedDocument().stats, null, 2)
      );
      this.props.navigateBack();
    };

    onRejectDocument = () => {
      this.props.showRejectDocumentDialog(this.props.requestedDocumentId);
    };

    onDocumentRejected = () => {
      if (this.props.oneDocumentMode) {
        gaeApp('SET_DOCUMENT_DONE_ON_REJECT');
        this.onDocumentDone(true);
      }
    };

    doUnRejectDocument = () => {
      /*this.props.removeR(SHOW_REJECT_DOCUMENT_DIALOG, {
        componentId: this.componentId,
        docId: this.props.requestedDocumentId,
      })*/
    };

    render() {
      const {
        requestedDocuments,
        requestedDocumentId,
        scanEnded,
        scannedPages
      } = this.props;

      const requestedDocument = requestedDocuments.find(
        doc => doc.id === requestedDocumentId
      );

      return (
        <WrappedComponent
          {...this.props}
          documentWasVisited={this.documentWasVisited}
          onRejectDocument={this.onRejectDocument}
          doUnRejectDocument={this.doUnRejectDocument}
          componentId={this.componentId}
          navigateBack={this.navigateBack}
          submitted={requestedDocument?.submitted}
          submittedDate={
            this.props.submittedDate || requestedDocument?.submittedDate
          }
          setDocumentDataUrl={this.props.setDocumentDataUrl}
          document={requestedDocument}
          scannedPages={scannedPages}
          scanEnded={
            scanEnded ||
            requestedDocument?.submitted ||
            requestedDocument?.rejected
          }
          onDocumentDone={this.onDocumentDone}
        />
      );
    }
  }

  withDocumentCommonWrapper.propTypes = {
    currentRequestPageIndex: PropTypes.number,
    requestPages: PropTypes.any,
    crossDeviceRequest: PropTypes.any,
    requestedDocuments: PropTypes.array,
    scanEnded: PropTypes.bool,
    submitted: PropTypes.bool,
    navigateBack: PropTypes.func,
    oneDocumentMode: PropTypes.bool,
    requestedDocumentId: PropTypes.string,
    callAction: PropTypes.func,
    forms: PropTypes.object,
    documentsStatus: PropTypes.object,
    onDocumentClose: PropTypes.func,
    doSendRequestFromDocumentView: PropTypes.func,
    templateMode: PropTypes.bool,
    isSkipDocumentListMode: PropTypes.bool,
    scannedPages: PropTypes.object,
    setDocumentDataUrl: PropTypes.func,
    setDocumentVisited: PropTypes.func,
    navigateToMobileDocument: PropTypes.func,
    navigateToRequestNextPageFromMobileDocument: PropTypes.func,
    navigateToRequestPrevPageFromMobileDocument: PropTypes.func,
    usersDataOk: PropTypes.bool,
    submittedDate: PropTypes.string,
    showRejectDocumentDialog: PropTypes.func,
    visitedDocuments: PropTypes.object,
    rejectedDocuments: PropTypes.object
  };

  const mapStateToProps = (state, ownProps) => {
    return {
      onDocumentClose: ownProps.onDocumentClose,
      scanEnded: state.scanEnded,
      requestCompleted: state.scanEnded,
      submittedDate: state.submittedDate,
      scannedPages:
        (state.scannedDocuments &&
          state.scannedDocuments.get(ownProps.requestedDocumentId)) ||
        new Map(),
      documentsStatus: state.documentsStatus,
      visitedDocuments: state.visitedDocuments,
      oneDocumentMode: state.oneDocumentMode,
      templateMode: state.templateMode,
      displayMode: ownProps.displayMode
        ? ownProps.displayMode
        : window.isDesktop
        ? DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW
        : DOCUMENT_DISPLAY_MODE_MOBILE_WINDOW
    };
  };

  const mapDispatchToProps = dispatch => ({
    setDocumentVisited: documentId => dispatch(setDocumentVisited(documentId)),
    unSetDocumentRejected: documentId =>
      dispatch(unSetDocumentRejected(documentId)),
    setDocumentDataUrl: (documentId, pageId, dataUrl) =>
      dispatch(setDocumentDataUrl(documentId, pageId, dataUrl))
  });

  return withDocumentRootLogic(
    xconnect(mapStateToProps, mapDispatchToProps)(withDocumentCommonWrapper)
  );
}
